import React, { FC, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { IClassNames, makeClassNames } from '../../../utils/styles';
import Text from '../../atoms/text/text';
import List from '../../atoms/list/list';
import { Color, FontSize, FontWeight } from '../../../typings/design-tokens';
import { IconArrowDown, IconArrowUp } from '../../atoms';

import './combo-box.scss';
import { attachScrollListener, scrollSelectedItem } from './combo-box-helpers';

const FONT_SIZE: FontSize = '14';
const FONT_WEIGHT: FontWeight = '500';
const LETTER_COLOR: Color = 'inherit';

export interface IComboOption {
  id?: string | null;
  name: string;
  url?: string | null;
}

export interface IComboBox {
  isLoading?: boolean;
  isDisabled?: boolean;
  options: Array<IComboOption>;
  placeholder: string;
  selected?: string;
  onClickCallback?: (arg0: IComboOption) => void;
  comboBoxID?: string | null;
  openedComboBoxID?: string | null;
  setOpenedComboBoxID?: (arg0: string | null) => void;
}

const classNames = (baseClass: string): IClassNames =>
  makeClassNames({
    comboBoxContainer: `${baseClass}__container`,
    comboBox: baseClass,
    isOpen: `${baseClass}--is-open`,
    optionSelected: `${baseClass}--option-selected`,
    optionList: `${baseClass}__container__option-list`,
    isDisabled: `${baseClass}--is-disabled`,
    isLoading: `${baseClass}--is-loading`,
    iconContainer: `${baseClass}__icon-container`,
  });

const ComboBox: FC<IComboBox> = ({
  /* istanbul ignore next: default value */ selected = '',
  /* istanbul ignore next: default value */ isLoading = false,
  /* istanbul ignore next: default value */ isDisabled = false,
  options,
  placeholder,
  onClickCallback,
  comboBoxID,
  setOpenedComboBoxID,
  openedComboBoxID,
}) => {
  const preselectedOptionIndex = options?.findIndex((option) => option.id === selected); // If there is no match it returns -1
  const preselectedOptionValue =
    preselectedOptionIndex !== -1 ? options.find((option) => option.id === selected)?.name : undefined;

  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(preselectedOptionIndex);
  const [value, setValue] = useState<string | undefined>(preselectedOptionValue);
  const [id, setId] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const refSelect: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const comboBoxRef = useRef<HTMLDivElement | null>(null); // New ref for the combo box button

  useEffect(() => {
    const cleanup = attachScrollListener(comboBoxRef, () => setShowOptions(false));
    return cleanup;
  }, []);
  const toggleComboBoxStatus = () => {
    if (!isLoading && !isDisabled) {
      setShowOptions((prevState) => !prevState);
      if (setOpenedComboBoxID) {
        setOpenedComboBoxID(comboBoxID as string);
      } // set this combo box as the opened one
    }
  };

  useEffect(() => {
    scrollSelectedItem(refSelect, 'auto');
  }, [showOptions]);

  useEffect(() => {
    scrollSelectedItem(refSelect);
  }, [activeSuggestionIndex]);

  const setValueByIndex = (index: number) => {
    setValue(options[index].name ?? '');
    setId(options[index].id ?? null);
    setUrl(options[index].url ?? null);
  };

  const onClick = (evt: MouseEvent<HTMLElement>) => {
    if (isDisabled) {
      return;
    }
    const index: number = Number((evt.target as HTMLElement).getAttribute('data-index') ?? '0');

    setValueByIndex(index);
    setActiveSuggestionIndex(index);
    setShowOptions(false);

    onClickCallback && onClickCallback(options[index]);
  };

  const selectFirstOption = () => {
    setActiveSuggestionIndex(0);
  };

  const onKeyDown = (evt: KeyboardEvent) => {
    if (showOptions) {
      switch (evt.key) {
        case 'ArrowUp':
          evt.preventDefault();
          if (activeSuggestionIndex === -1) {
            selectFirstOption();
          } else if (activeSuggestionIndex > 0) {
            setActiveSuggestionIndex(activeSuggestionIndex - 1);
          }
          break;
        case 'ArrowDown':
          evt.preventDefault();
          if (activeSuggestionIndex === -1) {
            selectFirstOption();
          } else if (activeSuggestionIndex < options?.length - 1) {
            setActiveSuggestionIndex(activeSuggestionIndex + 1);
          }
          break;
        case 'Escape':
          setShowOptions(false);
          break;
        case 'Enter':
          activeSuggestionIndex !== -1 && setValueByIndex(activeSuggestionIndex);
          onClickCallback && onClickCallback(options[activeSuggestionIndex]);
          break;
        default:
          break; // All other keys
      }
    }
  };

  useEffect(() => {
    if (comboBoxID !== openedComboBoxID) {
      setShowOptions(false); // close this combo box if another one is opened
    }
  }, [openedComboBoxID]);

  const cn = classNames('combo-box');
  return (
    <div ref={comboBoxRef} className={`${cn.comboBoxContainer}`}>
      <button
        className={`${cn.comboBox} ${isDisabled ? cn.isDisabled : ''} ${showOptions ? cn.isOpen : ''} ${
          value ? cn.optionSelected : ''
        }`}
        id={comboBoxID?.toString() || undefined}
        type="button"
        value={value}
        data-id={id}
        data-url={url}
        onClick={toggleComboBoxStatus}
        onKeyDown={onKeyDown}
        disabled={isDisabled}
      >
        <Text tag="span" fontSize={FONT_SIZE} color={LETTER_COLOR} fontWeight={FONT_WEIGHT}>
          {value || placeholder}
        </Text>
        {showOptions ? (
          <div className={`${cn.iconContainer}`}>
            <IconArrowUp size="xxxsmall" color={value ? 'light' : 'dark'} />
          </div>
        ) : (
          <div className={`${cn.iconContainer}`}>
            <IconArrowDown size="xxxsmall" color={value ? 'light' : 'dark'} />
          </div>
        )}
      </button>
      {showOptions && options?.length && (
        <div ref={refSelect} className={cn.optionList}>
          <List
            listItems={options?.map((suggestion: IComboOption) => suggestion.name)}
            activeItemIndex={activeSuggestionIndex}
            onClick={onClick}
          />
        </div>
      )}
    </div>
  );
};

ComboBox.propTypes = {
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  onClickCallback: PropTypes.func,
  options: PropTypes.arrayOf<IComboOption>(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      url: PropTypes.string,
    }).isRequired
  ).isRequired,
  placeholder: PropTypes.string.isRequired,
  selected: PropTypes.string,
};

export default ComboBox;
